/**
 * Central service initialization module.
 * This module initializes all application services with their required stores.
 * Initialization happens automatically when this module is imported.
 */

import { useCanvasStore } from '@/modules/canvas/store';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { useLodStore } from '@/modules/lod/useLodStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { FpsServiceFactory } from '@/modules/floorplanV2/service/FpsServiceFactory';
import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { useHistoryStore } from '@/modules/history/useHistoryStore';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';
import { AreaPickerServiceFactory } from '@/modules/canvas/services/AreaPicker/AreaPickerServiceFactory';
import { OrderProfileServiceFactory } from '@/modules/orderProfile/service/OrderProfileServiceFactory';
import KeyboardUtil from '@/modules/keyboard/KeyboardUtil';
import { FlowServiceFactory } from '@/modules/flowsV2/service/FlowServiceFactory';
import { useActiveFlowStore } from '@/modules/flowsV2/store/useActiveFlowStore';
import { DistconServiceFactory, useActiveDistconStore } from '@/modules/connections';
import { useOrderProfileStore } from '@/modules/orderProfile/store/useOrderProfileStore';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { useFpsStore } from '@/modules/floorplanV2/store/useFpsStore';
import { useNotificationStore } from '@/modules/Notifications/store/useNotificationStore';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { NotificationServiceFactory } from '@/modules/Notifications/service/NotificationServiceFactory';
import { SimulationServiceFactory } from '@/modules/simulation/service/SimulationServiceFactory';
import { useSimulationGroupStore } from '@/modules/simulation/useSimulationGroupStore';
import { useLayerStore } from '@/modules/layers/store/useLayerStore';
import { LayerServiceFactory } from '@/modules/layers/service/LayerServiceFactory';
import { ReferenceImageServiceFactory } from '@/modules/referenceImage/service/ReferenceImageServiceFactory';
import { useReferenceImageUIStore } from '@/modules/referenceImage/store';
import { OriginServiceFactory } from '@/modules/origin';
import { ContextMenuServiceFactory } from '@/modules/canvas/services/ContextMenu/ContextMenuServiceFactory';
import { useContextMenuStore } from '@/modules/contextMenu/useContextMenuStore';
import { useLoadCarrierStore } from '@/modules/loadCarriers/store/useLoadCarrierStore';
import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';
import { VehicleServiceFactory } from '@/modules/vehicles/service/VehicleServiceFactory';
import { LoadCarrierServiceFactory } from '@/modules/loadCarriers/service/LoadCarrierServiceFactory';
import { HideElementsService } from '@/modules/canvas/services';
import { CopyPasteServiceFactory, useCopyPasteStore } from '@/modules/copy';
import { usePointCloudUIStore } from '@/modules/pointCloud/store';
import { FleetTrackerPlaybackServiceFactory } from '@/modules/visualization/service/FleetTrackerPlaybackServiceFactory';
import { useFleetTrackerPlaybackStore } from '@/modules/visualization/store/useFleetTrackerPlaybackStore';
import { useFleetTrackerCanvasStore } from '@/modules/visualization/store/useFleetTrackerCanvasStore';
import { usePlayStore } from '@/modules/visualization/store/usePlayStore';
import { CollisionServiceFactory } from '@/modules/collision/service/CollisionServiceFactory';
import { useCollisionStore } from '@/modules/collision/store/useCollisionStore';

// Context & UI Services
ContextMenuServiceFactory.initialize(useContextMenuStore);
NotificationServiceFactory.initialize(useNotificationStore);
SnackbarServiceFactory.initialize(useSnackbarStore);

// Data Services
LayerServiceFactory.initialize(useLayerStore);
VehicleServiceFactory.initialize(useVehicleStore);
LoadCarrierServiceFactory.initialize(useLoadCarrierStore);

// Floorplan Design Services
FpsServiceFactory.initialize(useFpsStore, useFloorplanDesignStore);
FddmServiceFactory.initialize(
  useFloorplanDesignStore,
  useCanvasStore,
  useAppStore,
  useAppMetaStore,
  usePointCloudUIStore,
  useCollisionStore,
);
ReferenceImageServiceFactory.initialize(
  useFloorplanDesignStore,
  useCanvasStore,
  useReferenceImageUIStore,
);
OriginServiceFactory.initialize(useFloorplanDesignStore, useCanvasStore);
CollisionServiceFactory.initialize(useCollisionStore, useFloorplanDesignStore);

// Canvas Services
HistoryServiceFactory.initialize(useHistoryStore);
CanvasServiceFactory.initialize(
  useCanvasStore,
  useLodStore,
  useAppStore,
  useFloorplanDesignStore,
  useVehicleStore,
);
HideElementsService.initialize(useFloorplanDesignStore, useCanvasStore);

// Feature Services
CopyPasteServiceFactory.initialize(useCopyPasteStore, useAppStore, useFloorplanDesignStore);
FlowServiceFactory.initialize(useFloorplanDesignStore, useCanvasStore, useActiveFlowStore);
DistconServiceFactory.initialize(useFloorplanDesignStore, useCanvasStore, useActiveDistconStore);
SimulationServiceFactory.initialize(
  useSimulationGroupStore,
  useFloorplanDesignStore,
  useVehicleStore,
);
FleetTrackerPlaybackServiceFactory.initialize(
  useFleetTrackerPlaybackStore,
  useFleetTrackerCanvasStore,
  usePlayStore,
);

// Application Services
AppServiceFactory.initialize(
  useAppStore,
  useAppMetaStore,
  useVehicleStore,
  useLoadCarrierStore,
  useFloorplanDesignStore,
);
LoadingServiceFactory.initialize(useAppStore);
AreaPickerServiceFactory.initialize();
OrderProfileServiceFactory.initialize(useOrderProfileStore);

// Utilities
KeyboardUtil.initialize(useAppStore);
